import { DownloadIcon } from "@chakra-ui/icons";
import { Button, Icon } from "@chakra-ui/react";
import { FunctionComponent, useCallback } from "react";
import * as FSQ from "src/generated/hooks/form-service/queries";
import { useAvelaToast } from "src/hooks/useAvelaToast";
import { FormTemplateId } from "src/scenes/orgAdmin/formTemplates/types/common";
import { triggerDownload } from "src/services/dataTransfer";
import { useListMessagesTemplateContext } from "../context";
import { useSaveListChanges } from "../hooks/useSaveListChanges";

type Props = {
  formTemplateId: uuid;
  hasChanges: boolean;
  confirm: () => Promise<boolean>;
  isRankingEnabled: boolean;
};

export const ExportButton: FunctionComponent<Props> = ({
  formTemplateId,
  hasChanges,
  confirm,
  isRankingEnabled,
}) => {
  const toast = useAvelaToast();
  const { state, dispatch } = useListMessagesTemplateContext();
  const saveListChanges = useSaveListChanges(
    formTemplateId,
    isRankingEnabled,
    dispatch
  );

  const service = FSQ.useExportMessageTemplates(
    {
      path: {
        formTemplateId,
      },
      query: {
        fetchRichContent: "content",
      },
    },
    ["exportMessageTemplates", formTemplateId],
    {
      enabled: false,
    }
  );

  const handleOnExportClick = useCallback(async () => {
    // If there are unsaved changes, ask user to confirm
    if (hasChanges) {
      const proceed = await confirm();
      if (!proceed) {
        return;
      }
    }

    const loadingToastId = toast({
      title: `Export in progress`,
      description: `Download will start shortly`,
      status: "loading",
    });

    try {
      const draft = state
        ?.get("listMessages")
        ?.get(FormTemplateId(formTemplateId))?.draft;

      // Save changes if there are any, and wait for completion
      if (hasChanges && draft) {
        await saveListChanges(draft);
      }

      const response = await service.refetch();

      if (!response.data?.data) {
        throw new Error("No data");
      }

      triggerDownload(
        new Blob(
          [JSON.stringify(response.data?.data.messageTemplates, undefined, 2)],
          {
            type: "application/json",
          }
        ),
        "messageTemplates.json"
      );
      toast.close(loadingToastId);
      toast({
        title: "Message templates exported",
        status: "success",
      });
    } catch (e: any) {
      toast.close(loadingToastId);
      toast.genericError();
      console.error(e);
    }
  }, [
    hasChanges,
    toast,
    confirm,
    state,
    formTemplateId,
    service,
    saveListChanges,
  ]);

  return (
    <Button
      variant="solid"
      size="md"
      leftIcon={<Icon as={DownloadIcon} />}
      onClick={handleOnExportClick}
    >
      Export
    </Button>
  );
};
